/**
 * 데이터셋 트리의 "소속"과 "계보"를 해석하는 순수 함수 모음.
 *
 * 이 두 축을 혼동하기 쉬워 한 곳에 모아둔다:
 *
 *   소속(container) = parent_id + parent_kind
 *     프로젝트 → (없음) / 작업 → 프로젝트 / 데이터셋 → 작업
 *     parent_id 숫자만으로는 프로젝트 id 와 작업 id 가 겹칠 수 있어
 *     반드시 parent_kind 와 함께 봐야 한다.
 *
 *   계보(lineage) = parent_node_id
 *     데이터셋 버전 사이의 "이전 버전" 선언. 소속과 완전히 무관하다.
 *     같은 작업 아래에 있어도 계보가 이어지지 않은 독립 데이터셋이 있을 수 있다.
 *
 * 실제로 이 둘을 섞어 쓴 탓에 독립 데이터셋이 남의 계열에 붙어 보이는 버그가 있었다.
 */

export type DatasetNodeKind = 'project' | 'task' | 'dataset'

/** 트리 노드의 최소 형태 — 페이지/서비스의 타입 모두와 호환된다 */
export interface TreeNodeLike {
  id: number
  name: string
  kind: DatasetNodeKind
  parent_id?: number | null
  parent_kind?: 'project' | 'task'
  parent_node_id?: number | null
  description?: string | null
  created_at?: string
}

/** 소속을 하나의 키로 만든다. parent_kind 기본값 처리를 여기서만 한다. */
export function containerKeyOf(n: TreeNodeLike): string {
  return `${n.parent_kind ?? 'project'}-${n.parent_id ?? 'root'}`
}

/** 같은 컨테이너(같은 작업, 또는 같은 프로젝트 직속)에 속하는가 */
export function inSameContainer(a: TreeNodeLike, b: TreeNodeLike): boolean {
  return containerKeyOf(a) === containerKeyOf(b)
}

/** 이 노드가 해당 부모의 직속 자식인가 */
export function isChildOf(child: TreeNodeLike, parent: TreeNodeLike): boolean {
  const expectedKind = parent.kind === 'project' ? 'project' : 'task'
  return child.parent_id === parent.id && (child.parent_kind ?? 'project') === expectedKind
}

/** 부모 아래의 특정 종류 자식들 (중복 제거) */
export function childrenOfContainer<T extends TreeNodeLike>(
  all: T[],
  parent: TreeNodeLike,
  kind: DatasetNodeKind,
): T[] {
  return all
    .filter(n => n.kind === kind && isChildOf(n, parent))
    .filter((n, i, arr) => i === arr.findIndex(a => a.id === n.id))
}

/**
 * 선택한 데이터셋이 속한 계보 사슬 — 위(이전 버전들)와 아래(후속들, 분기 포함).
 * 최신순으로 정렬해 반환한다. 독립 데이터셋이면 자기 자신만 담긴다.
 */
export function lineageChainOf<T extends TreeNodeLike>(selected: T, all: T[]): T[] {
  const datasets = all.filter(n => n.kind === 'dataset')
  const byId = new Map(datasets.map(n => [n.id, n]))
  const chain: T[] = [selected]
  const seen = new Set<number>([selected.id])

  // 위로: 이전 버전들
  let cur: T | undefined = selected
  while (cur?.parent_node_id != null) {
    const p = byId.get(cur.parent_node_id)
    if (!p || seen.has(p.id)) break
    seen.add(p.id)
    chain.push(p)
    cur = p
  }
  // 아래로: 이 버전을 이전 버전으로 선언한 후속들 (분기 포함, 너비 우선)
  const queue: number[] = [selected.id]
  while (queue.length) {
    const id = queue.shift() as number
    for (const n of datasets) {
      if (n.parent_node_id === id && !seen.has(n.id)) {
        seen.add(n.id)
        chain.push(n)
        queue.push(n.id)
      }
    }
  }
  return sortByCreatedDesc(chain)
}

/**
 * 컨테이너 아래 데이터셋들을 계보 계열별로 묶는다.
 *  - chains: 2개 이상이 이어진 계열 (각각 최신순)
 *  - standalone: 계보가 없는 독립 데이터셋
 */
export function groupVersionsInContainer<T extends TreeNodeLike>(
  all: T[],
  parent: TreeNodeLike,
): { chains: T[][]; standalone: T[]; total: number } {
  const datasets = childrenOfContainer(all, parent, 'dataset')
  const ids = new Set(datasets.map(n => n.id))
  const childrenBy = new Map<number, T[]>()
  const roots: T[] = []

  for (const n of datasets) {
    const p = n.parent_node_id
    if (p != null && ids.has(p)) {
      childrenBy.set(p, [...(childrenBy.get(p) || []), n])
    } else {
      roots.push(n)
    }
  }

  const chains: T[][] = []
  const standalone: T[] = []
  for (const r of roots) {
    const acc: T[] = []
    const q: T[] = [r]
    while (q.length) {
      const cur = q.shift() as T
      acc.push(cur)
      for (const c of childrenBy.get(cur.id) || []) q.push(c)
    }
    if (acc.length === 1) standalone.push(acc[0])
    else chains.push(sortByCreatedDesc(acc))
  }
  chains.sort((a, b) => cmpCreatedDesc(a[0], b[0]))
  return { chains, standalone: sortByCreatedDesc(standalone), total: datasets.length }
}

function cmpCreatedDesc(a?: TreeNodeLike, b?: TreeNodeLike): number {
  return String(b?.created_at || '').localeCompare(String(a?.created_at || ''))
}

/** 최신순 정렬 (created_at 내림차순) */
export function sortByCreatedDesc<T extends TreeNodeLike>(list: T[]): T[] {
  return [...list].sort(cmpCreatedDesc)
}
